import React, { Suspense } from "react";
import { MainNav } from "./main-nav";
import { Spinner } from "@/components/ui/spinner";
import { AppSidebar } from "@/components/AppSidebar";
import { SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { InstrumentedCatchBoundaryGenericDisplay } from "@/components/error-boundary";
import { Outlet, useLocation, useMatches } from "@tanstack/react-router";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { FeedbackFooter } from "@/feedback/FeedbackFooter";

export function PageFrame() {
  const location = useLocation();
  const matches = useMatches();
  const { setOpenMobile } = useSidebar()
  const navRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    setOpenMobile(false);
  }, [location]);

  // Update CSS custom property when nav height changes
  React.useEffect(() => {
    if (!navRef.current) return

    const updateNavHeight = () => {
      const height = navRef.current?.offsetHeight ?? 0
      document.documentElement.style.setProperty('--nav-height', `${height}px`)
    }

    const observer = new ResizeObserver(updateNavHeight)
    observer.observe(navRef.current)
    updateNavHeight() // Initial calculation

    return () => observer.disconnect()
  }, [])

  const customNav = matches.find(match => match.context?.customNav)?.context?.customNav;
  const removeContentPadding = matches.find(match => match.context?.removeContentPadding)?.context?.removeContentPadding;
  const hideFeedbackFooter = matches.find(match => match.context?.hideFeedbackFooter)?.context?.hideFeedbackFooter;
  const hideNavBar = matches.find(match => match.context?.hideNavBar)?.context?.hideNavBar;

  return (
    <InstrumentedCatchBoundaryGenericDisplay>
      <AppSidebar/>
      <div className="grow flex flex-row">
        <div className="flex flex-col flex-1">
          <div
            ref={navRef}
            className={`max-w-[100vw] flex justify-center border-b border-thalo-border bg-white sticky top-0 left-0 right-0 z-50 transition-transform duration-200 translate-y-0 ${hideNavBar ? 'hidden' : ''}`}
          >
            <div className="flex items-center flex-grow self-center pl-2 lg:pl-4 max-w-[100vw]">
              <SidebarTrigger />
              {/* Breadcrumbs: hidden on mobile, visible on desktop */}
              <div
                className="hidden lg:block flex-grow overflow-hidden"
                style={{
                  maskImage: 'linear-gradient(to right, transparent, black 16px, black calc(100% - 16px), transparent)',
                  WebkitMaskImage: 'linear-gradient(to right, transparent, black 16px, black calc(100% - 16px), transparent)'
                }}
              >
                <ScrollArea className="w-full">
                  {customNav ? customNav() : <MainNav className="py-6 px-4"/>}
                  <ScrollBar orientation="horizontal"/>
                </ScrollArea>
              </div>
            </div>
          </div>
          <div
            className={`w-full max-w-[100vw] grow min-h-[calc(100vh-var(--nav-height))] custom-bg-gradient flex-1 space-y-4 self-center ${removeContentPadding ? '' : 'p-2 lg:p-8'}`}
            id="main-content"  // used for scroll restoration
          >
            <Suspense fallback={
              <div className="w-full h-full flex justify-center items-center">
                <Spinner/>
              </div>
            }>
              <InstrumentedCatchBoundaryGenericDisplay>
                <Outlet/>
              </InstrumentedCatchBoundaryGenericDisplay>
            </Suspense>
          </div>
          {
            !hideFeedbackFooter && (
              <div className="block lg:hidden">
                <FeedbackFooter/>
              </div>
            )
          }
        </div>
      </div>
    </InstrumentedCatchBoundaryGenericDisplay>
  );
}